import type { JSONSchema7, JSONSchema7Definition } from "json-schema"
import Validator, { ErrorObject, Ajv } from "ajv"
import { UniversalSchemaType } from "universal-schema/lib/0.0.4"
import { isJSONSchema7 } from "universal-schema/lib"
import { utils } from "@rjsf/core" // TODO: this is not good...

let ajv: Ajv | undefined

function getAjv(): Ajv {
    if (ajv !== undefined) {
        return ajv
    }

    ajv = new Validator({ allErrors: true, errorDataPath: "property" })

    return ajv
}

export const validate = (
    schema: JSONSchema7,
    data: any
): { valid: boolean; errors: ErrorObject[] } => {
    const ajv = getAjv()

    try {
        const valid = ajv.validate(
            { ...schema, $schema: undefined }, // schema ref isn't necessary for validation to work (and can cause issues)
            data
        ) as boolean // safe to cast to boolean because we don't pass async flag to ajv.compile()
        return {
            valid,
            errors: [...(ajv.errors || [])]
        }
    } catch (error) {
        return {
            valid: false,
            errors: [error as any]
        }
    }
}

/**
 * Find examples from schema.
 * If not found, check examples for object properties or array items recursively to construct examples.
 */
export function findExamples(schema: JSONSchema7, single: boolean): unknown[] {
    if (schema.examples !== undefined) {
        if (!Array.isArray(schema.examples)) {
            return []
        }

        const [first, ...rest] = schema.examples

        return [
            first,
            ...(single ? [] : schema.examples.length < 2 ? [first] : rest)
        ]
    }

    if (schema.properties !== undefined) {
        return Object.entries(schema.properties).reduce<{}[]>(
            (accum, [key, childSchema]) => {
                const childExamples = isJSONSchema7(childSchema)
                    ? findExamples(childSchema, single)
                    : []

                return Array.from({
                    length: Math.max(accum.length, childExamples.length)
                }).map((_, i) => ({
                    ...(accum[i] ?? {}),
                    [key]: childExamples[i]
                }))
            },
            []
        )
    }

    if (isJSONSchema7(schema.items)) {
        return [findExamples(schema.items, single)]
    }

    return []
}

/**
 * Returns `true` if schema expects nested object
 */
export function isNested(rootSchema: JSONSchema7): boolean {
    function isNestedInternal(
        schema: JSONSchema7Definition,
        isRoot: boolean
    ): boolean {
        if (!isJSONSchema7(schema)) {
            return false
        }

        if (schema.$ref !== undefined) {
            return isNestedInternal(
                utils.resolveSchema(schema, rootSchema),
                isRoot
            )
        }

        if (schema.anyOf !== undefined) {
            return schema.anyOf.some(s => isNestedInternal(s, isRoot))
        }

        if (schema.allOf !== undefined) {
            return schema.allOf.some(s => isNestedInternal(s, isRoot))
        }

        if (schema.oneOf !== undefined) {
            return schema.oneOf.some(s => isNestedInternal(s, isRoot))
        }

        if (schema.properties !== undefined) {
            return (
                !isRoot &&
                Object.values(schema.properties ?? {}).some(
                    s => !isNestedInternal(s, false)
                )
            )
        }

        return false
    }

    return isNestedInternal(rootSchema, true)
}
// find only one path to the image, does not not support multiple images as of now
// only nested object type is supported
export function findImageUrlPathForDownload(
    schema: JSONSchema7Definition | undefined
): string[] | undefined {
    if (!isJSONSchema7(schema)) {
        return
    }
    if (schema.$ref !== undefined) {
        switch (schema.$ref) {
            case UniversalSchemaType.IMAGE:
                return []
            case UniversalSchemaType.IMAGE_POLYGON:
                return ["imageUrl"]
            default:
                return
        }
    }
    if (schema.type !== "object" || schema.properties === undefined) {
        return
    }

    for (const [key, value] of Object.entries(schema.properties)) {
        const fieldNames = findImageUrlPathForDownload(value)
        if (fieldNames !== undefined) {
            return [key].concat(fieldNames)
        }
    }
    return
}

export function findDataUrlForDownload(
    formData: unknown,
    path: string[]
): string | undefined {
    if (typeof formData === "string" && path.length === 0) {
        return formData
    }
    if (
        typeof formData !== "object" ||
        formData === null ||
        path.length === 0
    ) {
        return
    }
    const pathRef = [...path]
    return findDataUrlForDownload((formData as any)[pathRef.shift()!], pathRef)
}

export function getAnnotationSchema(
    schema: JSONSchema7 | undefined
): JSONSchema7 | undefined {
    if (!schema) return undefined

    return schema.properties !== undefined &&
        "annotations" in schema.properties &&
        isJSONSchema7(schema.properties.annotations)
        ? utils.resolveSchema(schema.properties.annotations, schema)
        : undefined
}

export function hasTableContent(schema: JSONSchema7 | undefined): boolean {
    if (!schema) return false

    const annotations = getAnnotationSchema(schema)
    if (!annotations?.properties) return false

    return Object.values(annotations.properties).some(prop => {
        if (!isJSONSchema7(prop)) return false
        if (!isJSONSchema7(prop.items)) return false

        const item = utils.resolveSchema(prop.items, schema)

        return (
            isJSONSchema7(item.properties?.content) &&
            item.properties?.content.type === "object"
        )
    })
}

export type AnnotationTitleWithKeys = {
    fieldName: string
    title: string
}

export function getAnnotationTitlesWithKeys(
    schema: JSONSchema7 | undefined
): AnnotationTitleWithKeys[] {
    const annotations = getAnnotationSchema(schema)
    if (!annotations?.properties) return []

    return Object.entries(annotations.properties)
        .map(([fieldName, prop]) => {
            let title = undefined
            if (isJSONSchema7(prop)) title = prop.title

            return {
                fieldName,
                title
            }
        })
        .filter(value => value.title !== undefined) as AnnotationTitleWithKeys[]
}
